import type { SerializeFrom } from '~/types/serialize-from';
import { useEffect } from 'react';
import { useFetcher } from 'react-router';
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import {
    STATS_NUMBERS_ACTION,
    type loader,
} from '~/routes/actions.stats-numbers';
import { useIsGoodBot } from './use-is-good-bot';

const statsNumbersAtom = atomWithStorage<
    NonNullable<SerializeFrom<typeof loader>>['statsNumbers'] | null
>('stats-numbers', null);

/**
 * Fetch the stats number once per browser session.
 */
const isFetchedAtom = atom(false);
const isLoadedAtom = atom(false);

/**
 * Hook to get the number of items we have in each directory item category. The
 * data is fetched once per browser session and fetched from the client. The
 * data is `null` until the data is fetched.
 */
export function useStatsNumbers() {
    const fetcher = useFetcher<typeof loader>({ key: 'stats-numbers' });
    const isBot = useIsGoodBot();
    const [statsNumbers, setStatsNumbers] = useAtom(statsNumbersAtom);
    const [isFetched, setIsFetched] = useAtom(isFetchedAtom);
    const [isLoaded, setIsLoaded] = useAtom(isLoadedAtom);

    useEffect(() => {
        if (isBot || isLoaded) {
            return;
        }
        if (!fetcher.data && fetcher.state === 'idle' && !isFetched) {
            fetcher.load(STATS_NUMBERS_ACTION);
            setIsFetched(true);
        } else if (fetcher.data) {
            setStatsNumbers(fetcher.data.statsNumbers);
            setIsFetched(true);
            setIsLoaded(true);
        }
    }, [
        fetcher,
        isBot,
        isFetched,
        isLoaded,
        setIsFetched,
        setIsLoaded,
        setStatsNumbers,
    ]);

    return {
        statsNumbers,
    };
}
